import i18n from '../i18n';
import { api, apiUpload, ApiError, getAccessToken, tryRefresh } from './client';
import { requestTransferAuthorization } from '../lib/transferPolicy';
import type { MediaFileDto, SignedUrlDto } from './types';
import type { components } from './generated/openapi';

type WaveformDto = components['schemas']['WaveformDto'];

export class TransferPolicyDeniedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TransferPolicyDeniedError';
  }
}

async function authorizeUpload(file: File | Blob, name?: string): Promise<void> {
  const authorization = await requestTransferAuthorization({
    direction: 'upload',
    label: name || (file instanceof File ? file.name : i18n.t('misc2:mediaFile')),
    sizeBytes: file.size,
  });
  if (!authorization.allowed) {
    throw new TransferPolicyDeniedError(authorization.reason ?? i18n.t('misc2:mediaWifiRequired'));
  }
}

export function uploadImage(file: File): Promise<MediaFileDto> {
  return uploadMedia(file);
}

export async function uploadMedia(file: File | Blob, name?: string): Promise<MediaFileDto> {
  await authorizeUpload(file, name);
  const form = new FormData();
  form.append('file', file, name || (file instanceof File ? file.name : 'media'));
  return apiUpload<MediaFileDto>('/api/v1/media', form);
}

/**
 * Как uploadMedia, но через XMLHttpRequest вместо fetch — только у него есть событие
 * прогресса отправки тела запроса (`upload.onprogress`), нужное для индикатора 0-100% на
 * загрузке крупных вложений (видео с телефона может весить сотни МБ, см. CLAUDE.md —
 * лимит 500 MB). onProgress получает (loadedBytes, totalBytes) на каждое событие браузера —
 * троттлинг вызова (чтобы не дёргать React-state слишком часто) остаётся на стороне
 * вызывающего компонента, здесь его нет намеренно (модуль не должен решать за UI).
 */
export interface VideoTrim {
  start: number;
  end: number;
}

export async function uploadMediaWithProgress(
  file: File | Blob,
  name: string | undefined,
  onProgress: (loadedBytes: number, totalBytes: number) => void,
  trim?: VideoTrim,
  retry = true,
): Promise<MediaFileDto> {
  await authorizeUpload(file, name);
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append('file', file, name || (file instanceof File ? file.name : 'media'));
    // Обрезка (см. VideoTrimEditor.tsx) — сервер сам вырежет через ffmpeg (MediaService.upload),
    // клиент по-прежнему грузит файл целиком, границы передаются просто как поля формы.
    if (trim) {
      form.append('trimStartSec', String(trim.start));
      form.append('trimEndSec', String(trim.end));
    }
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/v1/media');
    const access = getAccessToken();
    if (access) xhr.setRequestHeader('Authorization', 'Bearer ' + access);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded, e.total);
    };
    xhr.onload = () => {
      if (xhr.status === 401 && retry) {
        // Тот же принцип single-flight refresh, что и в apiUpload/api (client.ts) — токен
        // мог протухнуть за время долгой загрузки крупного файла.
        tryRefresh().then((ok) => {
          if (ok) {
            uploadMediaWithProgress(file, name, onProgress, trim, false).then(resolve, reject);
          } else {
            reject(new ApiError(401, i18n.t('misc2:mediaAuthRequired')));
          }
        });
        return;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch {
          reject(new ApiError(xhr.status, i18n.t('misc2:mediaInvalidResponse')));
        }
      } else {
        let detail = xhr.statusText;
        try {
          const body = JSON.parse(xhr.responseText);
          detail = body.detail || body.title || detail;
        } catch {
          /* нет тела */
        }
        reject(new ApiError(xhr.status, detail));
      }
    };
    xhr.onerror = () => reject(new ApiError(0, i18n.t('misc2:mediaNetworkError')));
    xhr.send(form);
  });
}

export function signedUrl(id: string): Promise<SignedUrlDto> {
  return api<SignedUrlDto>(`/api/v1/media/${id}/signed-url`);
}

// Кэш по fileId — файл по id иммутабелен (см. архитектуру в CLAUDE.md), поэтому нет смысла
// заново тянуть один и тот же id при каждом маунте компонента. Кэшируем именно Promise (не
// готовый результат), чтобы несколько одновременных вызовов для одного id схлопывались в
// один сетевой запрос — тот же приём, что и в single-flight refresh в client.ts. Подпись
// ссылки короткоживущая (expiresIn с сервера), поэтому вместе со ссылкой храним момент
// истечения и перезапрашиваем заранее (SIGNED_URL_REFRESH_MARGIN_MS до конца), а не держим
// ссылку до закрытия вкладки.
const SIGNED_URL_REFRESH_MARGIN_MS = 60_000;
interface CachedSrc {
  promise: Promise<string>;
  /** Момент (Date.now(), мс), после которого ссылку нужно перезапросить; 0 — пока неизвестен. */
  refreshAt: number;
}
const imageSrcCache = new Map<string, CachedSrc>();
const thumbnailSrcCache = new Map<string, Promise<string>>();
const waveformRequests = new Map<string, Promise<WaveformDto>>();

const WAVEFORM_CACHE_PREFIX = 'hc_waveform:';
const WAVEFORM_REQUEST_TIMEOUT_MS = 15_000;

function isValidWaveform(value: unknown): value is WaveformDto {
  if (!value || typeof value !== 'object' || !('peaks' in value) || !Array.isArray(value.peaks)) return false;
  return value.peaks.length > 0 && value.peaks.length <= 128
    && value.peaks.every((peak) => typeof peak === 'number' && Number.isFinite(peak) && peak >= 0 && peak <= 1);
}

function readWaveformCache(id: string): WaveformDto | null {
  try {
    const raw = localStorage.getItem(WAVEFORM_CACHE_PREFIX + id);
    if (!raw) return null;
    const value: unknown = JSON.parse(raw);
    if (isValidWaveform(value)) return value;
    localStorage.removeItem(WAVEFORM_CACHE_PREFIX + id);
  } catch {
    // Хранилище браузера может быть отключено или заполнено — это только ускоряющий кэш.
  }
  return null;
}

/** Кэш пиков локально: валидный результат открывает волну без повторного запроса. */
export function cacheMediaWaveform(id: string, peaks: number[]): void {
  const waveform = { peaks };
  if (!isValidWaveform(waveform)) return;
  try {
    localStorage.setItem(WAVEFORM_CACHE_PREFIX + id, JSON.stringify(waveform));
  } catch {
    // Ошибка записи не должна менять работу плеера.
  }
}

/** Сначала постоянный кэш браузера, затем общий API-клиент с Bearer/refresh. */
export function getMediaWaveform(id: string): Promise<WaveformDto> {
  const cached = readWaveformCache(id);
  if (cached) return Promise.resolve(cached);
  const inFlight = waveformRequests.get(id);
  if (inFlight) return inFlight;

  const controller = new AbortController();
  const timeout = globalThis.setTimeout(() => controller.abort(), WAVEFORM_REQUEST_TIMEOUT_MS);
  const request = api<WaveformDto>(`/api/v1/media/${encodeURIComponent(id)}/waveform`, { signal: controller.signal })
    .then((waveform) => {
      if (!isValidWaveform(waveform) || waveform.peaks.length < 64) {
        throw new Error('Некорректный ответ с пиками аудио');
      }
      cacheMediaWaveform(id, waveform.peaks);
      return waveform;
    })
    .finally(() => {
      globalThis.clearTimeout(timeout);
      waveformRequests.delete(id);
    });
  waveformRequests.set(id, request);
  return request;
}

function loadSignedSrc(id: string): CachedSrc {
  const entry: CachedSrc = { promise: Promise.resolve(''), refreshAt: 0 };
  const requestedAt = Date.now();
  entry.promise = signedUrl(id).then((su) => {
    entry.refreshAt = requestedAt + su.expiresIn * 1000 - SIGNED_URL_REFRESH_MARGIN_MS;
    return su.url;
  });
  // Неудачный запрос не кэшируем — иначе один временный сбой сети "хоронил" бы файл
  // до перезагрузки вкладки.
  entry.promise.catch(() => {
    if (imageSrcCache.get(id) === entry) imageSrcCache.delete(id);
  });
  imageSrcCache.set(id, entry);
  return entry;
}

/** Короткоживущий URL для <img src> (без Authorization). Перезапрашивается заранее до истечения подписи. */
export function imageSrc(id: string): Promise<string> {
  const cached = imageSrcCache.get(id);
  if (cached && (cached.refreshAt === 0 || Date.now() < cached.refreshAt)) return cached.promise;
  return loadSignedSrc(id).promise;
}

/** Принудительно новая подписанная ссылка (например, после ошибки загрузки <video>/<audio>). */
export function refreshImageSrc(id: string): Promise<string> {
  return loadSignedSrc(id).promise;
}

// Осторожная предзагрузка небольшой части аудио (первые 64 КБ) через Range, чтобы браузерный
// кэш прогрелся до нажатия play. Полный файл не скачивается, воспроизведение не запускается.
const AUDIO_PREFETCH_BYTES = 64 * 1024;
const prefetchedAudio = new Set<string>();
const audioPrefetchRequests = new Map<string, Promise<void>>();
export async function prefetchAudioRange(id: string, sizeBytes?: number | null): Promise<void> {
  if (!id || prefetchedAudio.has(id)) return;
  const inFlight = audioPrefetchRequests.get(id);
  if (inFlight) return inFlight;
  const requestedBytes = Math.min(Math.max(1, sizeBytes ?? AUDIO_PREFETCH_BYTES), AUDIO_PREFETCH_BYTES);
  const request = (async () => {
    const authorization = await requestTransferAuthorization({
      direction: 'download',
      label: 'audio',
      sizeBytes: requestedBytes,
    });
    if (!authorization.allowed) return;
    try {
      const url = await imageSrc(id);
      const response = await fetch(url, { headers: { Range: `bytes=0-${requestedBytes - 1}` } });
      // Не принимать 200: сервер/прокси мог проигнорировать Range и прислать целый файл.
      const range = response.headers.get('Content-Range');
      if (response.status !== 206 || !range || !/^bytes 0-\d+\/(?:\d+|\*)$/i.test(range)) {
        await response.body?.cancel();
        return;
      }
      const reader = response.body?.getReader();
      if (!reader) return;
      let received = 0;
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          received += value.byteLength;
          if (received > requestedBytes) {
            await reader.cancel();
            return;
          }
        }
      } finally {
        reader.releaseLock();
      }
      if (received > 0) prefetchedAudio.add(id);
    } catch {
      /* предзагрузка best-effort: ошибка не должна мешать обычному воспроизведению */
    }
  })();
  audioPrefetchRequests.set(id, request);
  try {
    await request;
  } finally {
    if (audioPrefetchRequests.get(id) === request) audioPrefetchRequests.delete(id);
  }
}

/**
 * Обработчик onError для <video>/<audio> с подписанной ссылкой: если ссылка успела протухнуть,
 * один раз (на конкретный src) запрашивает новую и подставляет её через setSrc.
 */
export function makeMediaErrorRetry(
  fileId: string | undefined,
  currentSrc: string | null | undefined,
  setSrc: (url: string) => void,
): () => void {
  return () => {
    if (!fileId || !currentSrc) return;
    if (retriedSrcs.has(currentSrc)) return;
    retriedSrcs.add(currentSrc);
    refreshImageSrc(fileId)
      .then((u) => {
        if (u !== currentSrc) setSrc(u);
      })
      .catch(() => {});
  };
}
const retriedSrcs = new Set<string>();

/**
 * Миниатюра (256px, эндпоинт /thumbnail — не публичный, как /content, поэтому не годится для
 * <img src> напрямую) — грузим авторизованным fetch и отдаём blob-URL. Используется в ленте
 * чата ВМЕСТО полноразмерного оригинала: полный файл подгружается только при открытии
 * лайтбокса (см. MediaImage в ChatView.tsx) — иначе каждое отрисованное сообщение с фото
 * сразу тянуло бы оригинал целиком, даже если пользователь его не разворачивал.
 *
 * Результат (включая сам blob-URL) кэшируется по fileId — см. imageSrcCache выше. Из-за этого
 * ВАЖНО: вызывающий код больше НЕ должен звать URL.revokeObjectURL на полученный URL —
 * он общий и может использоваться несколькими одновременно смонтированными виджетами одного и
 * того же fileId (например, лента чата + галерея инфопанели разом), ревокация в одном месте
 * сломала бы <img>/постер в другом. Blob-URL просто живёт до закрытия вкладки — тот же
 * компромисс, что и для промис-кэша выше.
 */
export function thumbnailSrc(id: string): Promise<string> {
  let cached = thumbnailSrcCache.get(id);
  if (!cached) {
    cached = (async () => {
      const access = getAccessToken();
      const res = await fetch(`/api/v1/media/${id}/thumbnail`, {
        headers: access ? { Authorization: 'Bearer ' + access } : {},
      });
      if (!res.ok) throw new ApiError(res.status, res.statusText);
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    })();
    cached.catch(() => thumbnailSrcCache.delete(id));
    thumbnailSrcCache.set(id, cached);
  }
  return cached;
}

/** Распознавание речи (голосовое/аудио-сообщение) — по кнопке, результат кэшируется на сервере. */
export function transcribe(id: string): Promise<{ text: string }> {
  return api<{ text: string }>(`/api/v1/media/${id}/transcribe`, { method: 'POST' });
}

/** Готовая транскрипция из кэша без запуска whisper — null, если расшифровка ещё не делалась
 *  (см. отчёт пользователя: текст пропадал после обновления страницы). */
export async function getTranscriptCached(id: string): Promise<{ text: string } | null> {
  try {
    return await api<{ text: string }>(`/api/v1/media/${id}/transcript`);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}
